import React, { useState, useEffect } from 'react';
import {
  Camera,
  Monitor,
  Sun,
  Moon,
  Check,
  Mail,
  User as UserIcon,
  Highlighter,
  ChevronDown,
  PanelTop,
  Languages,
  Palette,
  Keyboard,
  Settings as SettingsIcon,
  Loader2,
  Layers,
  Accessibility,
} from 'lucide-react';
import { AiPreferencesPanel } from '@/components/settings/AiPreferencesPanel';
import { SessionContextSettings } from '@/components/settings/SessionContextSettings';
import { WorkMemorySettings } from '@/components/settings/WorkMemorySettings';
import { AccountDangerZone } from '@/components/settings/AccountDangerZone';
import { ApiKeysSettings } from '@/components/settings/ApiKeysSettings';
import { McpIntegrationsSettings } from '@/components/settings/McpIntegrationsSettings';
import { AiByokSettings } from '@/components/settings/AiByokSettings';
import { TeamChatSettings } from '@/components/settings/TeamChatSettings';
import { TeamSkillsSettings } from '@/components/settings/TeamSkillsSettings';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Theme,
  PRIMARY_COLOR_OPTIONS,
  PRIMARY_COLOR_TOKENS,
  type PrimaryColor,
} from '@/stores/theme-store';
import { useThemePreference } from '@/hooks/useThemePreference';
import { useA11yPreferencesStore } from '@/stores/a11y-preferences-store';
import { auth } from '@/firebase';
import { useAuth } from '@/auth/AuthContext';
import { notifications } from '@/utils/notifications';
import { normalizeLocale, SUPPORTED_LOCALE_CODES, type AppLocale } from '@/i18n/supported-languages';

// --- Sub-components ---

interface SectionProps {
  icon: React.ElementType;
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

const Section = ({ icon: Icon, title, description, children, className, contentClassName }: SectionProps) => (
  <section className={clsx('space-y-4', className)}>
    <header className="flex items-baseline gap-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-bold text-foreground">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </header>
    <div className={clsx('rounded-2xl bg-card p-5 sm:p-6', contentClassName)}>
      {children}
    </div>
  </section>
);

const settingsFieldClass =
  'w-full rounded-xl border-0 bg-muted/50 px-3 py-2.5 text-sm font-medium text-foreground transition-all placeholder:text-muted-foreground/70 focus:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:opacity-50';

const settingsFieldReadonlyClass =
  'w-full cursor-not-allowed rounded-xl border-0 bg-muted/30 px-3 py-2.5 text-sm font-medium text-muted-foreground';

const ThemeChip = ({
  id,
  label,
  active,
  onClick,
}: {
  id: Theme;
  label: string;
  active: boolean;
  onClick: () => void;
}) => {
  const Icon = id === 'light' ? Sun : id === 'dark' ? Moon : Monitor;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'group flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
        active
          ? 'bg-primary/10 text-foreground ring-2 ring-primary ring-offset-2 ring-offset-card'
          : 'bg-muted/40 text-muted-foreground hover:bg-muted/60 hover:text-foreground'
      )}
    >
      <Icon size={16} className={clsx(active ? 'text-primary' : 'text-muted-foreground/70')} />
      <span>{label}</span>
      {active && (
        <span className="grid h-4 w-4 place-items-center rounded-full bg-primary text-white">
          <Check size={10} />
        </span>
      )}
    </button>
  );
};

const PrimaryColorSwatch = ({
  id,
  label,
  active,
  disabled,
  onClick,
}: {
  id: PrimaryColor;
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const token = PRIMARY_COLOR_TOKENS[id];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={clsx(
        'group relative grid h-9 w-9 place-items-center rounded-full ring-2 transition-all disabled:opacity-50',
        active ? 'ring-primary' : 'ring-transparent hover:ring-border-muted'
      )}
    >
      <span
        className="h-7 w-7 rounded-full shadow-sm"
        style={{ backgroundColor: `hsl(${token.primary})` }}
      />
      {active && (
        <span className="absolute inset-0 grid place-items-center text-white">
          <Check size={14} />
        </span>
      )}
    </button>
  );
};

interface ToggleRowProps {
  icon?: React.ElementType;
  title: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void | Promise<void>;
  ariaLabel: string;
}

const ToggleRow = ({ icon: Icon, title, description, checked, disabled, onChange, ariaLabel }: ToggleRowProps) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <div className="flex min-w-0 flex-1 items-start gap-3">
      {Icon ? (
        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
          <Icon size={14} />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description ? (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => void onChange()}
      className={clsx(
        'relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-muted-foreground/30'
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </button>
  </div>
);

const ShortcutItem = ({
  action,
  keys,
  keyThenLabel,
}: {
  action: string;
  keys: string[];
  keyThenLabel: string;
}) => (
  <div className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-muted/40">
    <span className="text-sm font-medium text-foreground">{action}</span>
    <div className="flex items-center gap-1">
      {keys.map((k, i) => (
        <React.Fragment key={`${k}-${i}`}>
          <kbd className="min-w-[26px] rounded-md bg-muted/60 px-1.5 py-0.5 text-center text-[10px] font-semibold text-foreground">
            {k}
          </kbd>
          {i < keys.length - 1 && (
            <span className="text-[10px] font-bold text-muted-foreground/60">{keyThenLabel}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

type ProfileTabId = 'general' | 'ai' | 'integrations' | 'mcp' | 'browsing' | 'team';

const PROFILE_TAB_IDS: ProfileTabId[] = ['general', 'ai', 'integrations', 'mcp', 'browsing', 'team'];

const LEGACY_HASH_TO_TAB: Record<string, ProfileTabId> = {
  'ai-privacy': 'ai',
  browsing: 'browsing',
  'session-context': 'browsing',
  'api-keys': 'mcp',
  'mcp-servers': 'mcp',
  byok: 'integrations',
  integrations: 'mcp',
  'team-chat-settings': 'team',
};

const MERGED_INTO_GENERAL_TABS = new Set(['appearance', 'shortcuts']);

function resolveProfileTab(tabParam: string | null): ProfileTabId {
  if (
    tabParam === 'ai' ||
    tabParam === 'integrations' ||
    tabParam === 'mcp' ||
    tabParam === 'browsing' ||
    tabParam === 'team'
  ) {
    return tabParam;
  }
  return 'general';
}

// --- Main Section ---

export const AccountSettings = () => {
  const SHOW_PROFILE_UPLOAD = false;
  const { theme, setTheme, primaryColor, setPrimaryColor } = useThemePreference();
  const setA11yMenuOpen = useA11yPreferencesStore((s) => s.setMenuOpen);
  const { user, updatePreference, updateDisplayName } = useAuth();
  const { t: ts } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const { t: tToast } = useTranslation('notifications');
  const [displayName, setDisplayName] = useState('');
  const [savedDisplayName, setSavedDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const [savingHighlighter, setSavingHighlighter] = useState(false);
  const [savingPageToolsFab, setSavingPageToolsFab] = useState(false);
  const [savingLocale, setSavingLocale] = useState(false);
  const [savingPrimaryColor, setSavingPrimaryColor] = useState(false);
  const [hiddenFabHosts, setHiddenFabHosts] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = resolveProfileTab(tabParam);

  const highlighterEnabled = user?.preference?.highlighter !== false;
  const pageToolsFabEnabled = user?.preference?.pageToolsFab !== false;
  const isExtensionApp =
    typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
  const uiLocale: AppLocale = normalizeLocale(user?.preference?.locale);

  useEffect(() => {
    const u = auth.currentUser;
    const nameFromUser = user?.name?.trim();
    const emailName =
      u?.email?.split('@')[0]?.replace(/[._-]+/g, ' ')?.replace(/\b\w/g, (c) => c.toUpperCase()) || '';
    const resolvedName = nameFromUser || u?.displayName?.trim() || emailName;
    if (resolvedName) {
      setDisplayName(resolvedName);
      setSavedDisplayName(resolvedName);
    }
    if (u?.email) setEmail(u.email);
    if (u?.photoURL) setPhotoURL(u.photoURL);
  }, [user?.name]);

  const displayNameDirty = displayName.trim() !== savedDisplayName.trim();

  const saveDisplayName = async () => {
    if (!user || savingDisplayName || !displayNameDirty) return;
    const trimmed = displayName.trim();
    if (!trimmed) {
      notifications.error(tToast('account.displayNameRequired'));
      setDisplayName(savedDisplayName);
      return;
    }
    try {
      setSavingDisplayName(true);
      await updateDisplayName(trimmed);
      setDisplayName(trimmed);
      setSavedDisplayName(trimmed);
      notifications.success(tToast('account.displayNameUpdated'));
    } catch (err) {
      setDisplayName(savedDisplayName);
      notifications.error(
        err instanceof Error ? err.message : tToast('account.displayNameSaveFailed')
      );
    } finally {
      setSavingDisplayName(false);
    }
  };

  const selectTab = (tab: ProfileTabId) => {
    if (tab === 'general') {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ tab }, { replace: true });
  };

  useEffect(() => {
    if (!tabParam || !MERGED_INTO_GENERAL_TABS.has(tabParam)) return;
    setSearchParams({}, { replace: true });
  }, [tabParam, setSearchParams]);

  useEffect(() => {
    const legacyHash = window.location.hash.replace(/^#/, '');
    const legacyTab = LEGACY_HASH_TO_TAB[legacyHash];
    if (!legacyTab) return;
    setSearchParams({ tab: legacyTab }, { replace: true });
    const scrollTargets = new Set([
      'ai-privacy',
      'session-context',
      'api-keys',
      'mcp-servers',
      'byok',
      'integrations',
      'team-chat-settings',
    ]);
    if (scrollTargets.has(legacyHash)) {
      const scrollId = legacyHash === 'integrations' ? 'api-keys' : legacyHash;
      requestAnimationFrame(() => {
        document.getElementById(scrollId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [setSearchParams]);

  useEffect(() => {
    if (!isExtensionApp) return;
    try {
      const w = window as unknown as {
        chrome?: {
          storage?: {
            sync?: {
              get: (keys: string[], cb: (r: { pageToolsFabHiddenHosts?: string[] }) => void) => void;
            };
          };
        };
      };
      w.chrome?.storage?.sync?.get(['pageToolsFabHiddenHosts'], (r) => {
        setHiddenFabHosts(Array.isArray(r?.pageToolsFabHiddenHosts) ? r.pageToolsFabHiddenHosts : []);
      });
    } catch {
      setHiddenFabHosts([]);
    }
  }, [isExtensionApp]);

  const initials = (displayName || email || 'T')
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <nav
        className="-mx-1 sticky top-0 z-10 flex gap-0.5 overflow-x-auto border-b border-border-muted bg-background pb-px scrollbar-thin"
        role="tablist"
        aria-label={ts('account.tabsAria')}
      >
        {PROFILE_TAB_IDS.map((tabId) => (
          <button
            key={tabId}
            type="button"
            role="tab"
            aria-selected={activeTab === tabId}
            onClick={() => selectTab(tabId)}
            className={clsx(
              'shrink-0 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              activeTab === tabId
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {ts(`account.tabs.${tabId}`)}
          </button>
        ))}
      </nav>

      {activeTab === 'general' && (
      <>
      <Section icon={UserIcon} title={ts('account.title')}>
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div className="relative shrink-0">
            {photoURL ? (
              <img
                src={photoURL}
                alt="Profile"
                className="h-16 w-16 rounded-2xl object-cover ring-2 ring-primary/10"
              />
            ) : (
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 text-lg font-bold text-primary ring-2 ring-primary/10">
                {initials || 'T'}
              </div>
            )}
            {SHOW_PROFILE_UPLOAD && (
              <button
                type="button"
                className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full bg-primary text-white shadow"
                aria-label={ts('account.profilePicture.upload')}
              >
                <Camera size={12} />
              </button>
            )}
          </div>
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
                {ts('account.displayName')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={displayName}
                  disabled={!user || savingDisplayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onBlur={() => void saveDisplayName()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setDisplayName(savedDisplayName);
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  className={clsx(settingsFieldClass, 'pr-9')}
                />
                {savingDisplayName ? (
                  <Loader2
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                ) : null}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
                {ts('account.emailAddress')}
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60"
                  size={14}
                />
                <input
                  readOnly
                  type="email"
                  value={email || ''}
                  className={clsx(settingsFieldReadonlyClass, 'pl-9')}
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section icon={Layers} title={ts('skills.title')} description={ts('skills.subtitle')}>
        <TeamSkillsSettings />
      </Section>

      <Section icon={Palette} title={ts('account.appearance')}>
        <div className="space-y-6">
          {/* Theme chips */}
          <div>
            <h4 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
              {ts('account.appearance')}
            </h4>
            <div className="flex flex-col gap-2 sm:flex-row">
              <ThemeChip
                id="light"
                label={tc('common.theme.light')}
                active={theme === 'light'}
                onClick={() => setTheme('light')}
              />
              <ThemeChip
                id="dark"
                label={tc('common.theme.dark')}
                active={theme === 'dark'}
                onClick={() => setTheme('dark')}
              />
              <ThemeChip
                id="system"
                label={tc('common.theme.system')}
                active={theme === 'system'}
                onClick={() => setTheme('system')}
              />
            </div>
          </div>

          {/* Primary color + language in 2 cols */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h4 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
                {ts('account.primaryColor.label')}
              </h4>
              <div className="flex flex-wrap items-center gap-2">
                {PRIMARY_COLOR_OPTIONS.map((option) => {
                  const previousColor = primaryColor;
                  return (
                    <PrimaryColorSwatch
                      key={option.id}
                      id={option.id}
                      label={ts(option.labelKey)}
                      active={primaryColor === option.id}
                      disabled={!user || savingPrimaryColor}
                      onClick={async () => {
                        if (!user || savingPrimaryColor || option.id === primaryColor) return;
                        try {
                          setSavingPrimaryColor(true);
                          setPrimaryColor(option.id);
                          await updatePreference({ primaryColor: option.id });
                          notifications.success(tToast('account.primaryColorUpdated'));
                        } catch (err) {
                          setPrimaryColor(previousColor);
                          notifications.error(
                            err instanceof Error ? err.message : tToast('account.primaryColorSaveFailed')
                          );
                        } finally {
                          setSavingPrimaryColor(false);
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
                <Languages size={12} />
                {ts('account.language.label')}
              </h4>
              <div className="relative">
                <select
                  value={uiLocale}
                  disabled={!user || savingLocale}
                  onChange={async (e) => {
                    const code = e.target.value as AppLocale;
                    if (!user || savingLocale || code === uiLocale) return;
                    try {
                      setSavingLocale(true);
                      await updatePreference({ locale: code });
                      notifications.success(
                        code === 'en'
                          ? tToast('account.languageSetEnglish')
                          : tToast('account.languageUpdated')
                      );
                    } catch (err) {
                      notifications.error(
                        err instanceof Error ? err.message : tToast('account.languageSaveFailed')
                      );
                    } finally {
                      setSavingLocale(false);
                    }
                  }}
                  className={clsx(settingsFieldClass, 'cursor-pointer appearance-none pr-9')}
                >
                  {SUPPORTED_LOCALE_CODES.map((code) => (
                    <option key={code} value={code}>
                      {tc(`common.language.${code}`)}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section icon={Accessibility} title={ts('accessibility.title')}>
        <p className="mb-3 text-sm text-muted-foreground">{ts('accessibility.intro')}</p>
        <button
          type="button"
          onClick={() => setA11yMenuOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl border-0 bg-muted/50 px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/70"
        >
          <Accessibility size={16} aria-hidden />
          {ts('accessibility.openFromProfile')}
        </button>
      </Section>

      <Section icon={Keyboard} title={ts('account.shortcuts.title')} contentClassName="p-2 sm:p-3">
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          <ShortcutItem
            action={ts('account.shortcuts.searchQuickOpen')}
            keys={['Cmd', 'K']}
            keyThenLabel={ts('account.shortcuts.keyThen')}
          />
          <ShortcutItem
            action={ts('account.shortcuts.createNote')}
            keys={['N']}
            keyThenLabel={ts('account.shortcuts.keyThen')}
          />
          <ShortcutItem
            action={ts('account.shortcuts.trovesView')}
            keys={['G', 'T']}
            keyThenLabel={ts('account.shortcuts.keyThen')}
          />
          <ShortcutItem
            action={ts('account.shortcuts.openSettings')}
            keys={['Cmd', ',']}
            keyThenLabel={ts('account.shortcuts.keyThen')}
          />
        </div>
      </Section>
      </>
      )}

      {activeTab === 'team' && <TeamChatSettings embedded />}

      {activeTab === 'ai' && (
        <>
          <AiPreferencesPanel embedded />
          <WorkMemorySettings />
        </>
      )}

      {activeTab === 'integrations' && <AiByokSettings embedded />}

      {activeTab === 'mcp' && (
        <div className="space-y-10">
          <ApiKeysSettings embedded />
          <McpIntegrationsSettings embedded />
        </div>
      )}

      {activeTab === 'browsing' && (
      <>
      <Section
        icon={Layers}
        title={tc('sessions.contextGuard.settingsTitle')}
        description={tc('sessions.contextGuard.settingsDescription')}
      >
        <SessionContextSettings embedded />
      </Section>

      <Section
        icon={SettingsIcon}
        title={ts('account.pageTools.title')}
        description={ts('account.pageTools.description')}
        contentClassName="p-2 sm:p-2"
      >
        <div className="divide-y divide-border-muted">
          <div className="px-3">
            <ToggleRow
              icon={Highlighter}
              title={ts('account.highlighter.heading')}
              description={ts('account.highlighter.description')}
              checked={highlighterEnabled}
              disabled={savingHighlighter || !user}
              ariaLabel={ts('account.highlighter.toggleAria')}
              onChange={async () => {
                if (!user || savingHighlighter) return;
                try {
                  setSavingHighlighter(true);
                  await updatePreference({ highlighter: !highlighterEnabled });
                  notifications.success(
                    !highlighterEnabled
                      ? tToast('account.highlighterEnabled')
                      : tToast('account.highlighterDisabled')
                  );
                } catch (e) {
                  notifications.error(
                    e instanceof Error ? e.message : tToast('account.highlighterSaveFailed')
                  );
                } finally {
                  setSavingHighlighter(false);
                }
              }}
            />
          </div>
          <div className="px-3">
            <ToggleRow
              icon={PanelTop}
              title={ts('account.pageTools.heading')}
              description={ts('account.pageTools.description')}
              checked={pageToolsFabEnabled}
              disabled={savingPageToolsFab || !user}
              ariaLabel={ts('account.pageTools.toggleAria')}
              onChange={async () => {
                if (!user || savingPageToolsFab) return;
                try {
                  setSavingPageToolsFab(true);
                  await updatePreference({ pageToolsFab: !pageToolsFabEnabled });
                  notifications.success(
                    !pageToolsFabEnabled
                      ? tToast('account.pageToolsFabEnabled')
                      : tToast('account.pageToolsFabDisabled')
                  );
                } catch (e) {
                  notifications.error(
                    e instanceof Error ? e.message : tToast('account.pageToolsFabSaveFailed')
                  );
                } finally {
                  setSavingPageToolsFab(false);
                }
              }}
            />
          </div>
        </div>
      </Section>

      {/* Extension-only extras */}
      {isExtensionApp && (
        <Section icon={PanelTop} title={ts('account.pageTools.resetPosition')}>
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">{ts('account.pageTools.resetPositionHelp')}</p>
              <button
                type="button"
                className="rounded-xl border-0 bg-muted/50 px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted/70"
                onClick={() => {
                  try {
                    const w = window as unknown as {
                      chrome?: {
                        storage?: {
                          sync?: { remove: (keys: string[], cb?: () => void) => void };
                          local?: { remove: (keys: string[], cb?: () => void) => void };
                        };
                      };
                    };
                    w.chrome?.storage?.sync?.remove(['pageToolsFabPosition'], () => undefined);
                    w.chrome?.storage?.local?.remove(
                      ['pageToolsFabPositionsByHost'],
                      () => undefined
                    );
                    notifications.success(tToast('account.pageToolsResetPosition'));
                  } catch {
                    notifications.error(tToast('account.pageToolsResetFailed'));
                  }
                }}
              >
                {ts('account.pageTools.resetPosition')}
              </button>
            </div>
            <div className="space-y-2 border-t border-border-muted pt-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/80">
                {ts('account.pageTools.hiddenSitesTitle')}
              </p>
              {hiddenFabHosts.length === 0 ? (
                <p className="text-xs text-muted-foreground">{ts('account.pageTools.hiddenSitesEmpty')}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {hiddenFabHosts.map((host) => (
                    <li key={host}>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-full bg-muted/50 px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/70"
                        onClick={() => {
                          const next = hiddenFabHosts.filter((h) => h !== host);
                          setHiddenFabHosts(next);
                          try {
                            const w = window as unknown as {
                              chrome?: { storage?: { sync?: { set: (o: object, cb?: () => void) => void } } };
                            };
                            w.chrome?.storage?.sync?.set({ pageToolsFabHiddenHosts: next });
                          } catch {
                            /* ignore */
                          }
                        }}
                      >
                        {host}
                        <span className="text-muted-foreground">×</span>
                        <span className="sr-only">{ts('account.pageTools.removeHost')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Section>
      )}
      </>
      )}

      {activeTab === 'general' && (
        <div className="pt-2">
          <AccountDangerZone />
        </div>
      )}
    </div>
  );
};
